/**
 * "Scan to join" flyer for an event CTA: the sales team's printable template with the CTA's saved QR
 * code dropped into its white box, saved to the event's Files.
 *
 * Go-forward only (owner, 2026-09-26): nothing here backfills. A flyer is made when an operator clicks
 * "Create flyer" on a CTA of an event that has not ended, or right after they prepare the event CTA.
 *
 * ⭐ The flyer ADVERTISES an instant credit, so it shows the CTA's OWN voucher amount (owner,
 * 2026-09-26: "make the value in the flyer match the value in the CTA"). A $20 CTA uses the original
 * artwork untouched; any other amount uses a copy of the artwork with "$20" removed
 * (scripts/flyer/make_blank_amount_template.py) and draws the amount in Inter Tight Black, the
 * closest OFL match to the artwork's lettering. A CTA with no voucher is refused — on PROD most recent
 * generic CTAs carry none, and a flyer would promise a credit the QR never issues.
 *
 * ⭐ INSURANCE-STATE EVENTS (migration 2026-10-09-410 rev 2; owner 2026-10-09: "insurance events must not advertise
 * $20"): a subscription CTA of an event whose venue is in an insurance state (see ctaClassroom.ts) uses an insurance
 * artwork and NEVER draws an amount, whatever the CTA's voucher_amount, and it is not refused for lacking a voucher.
 * finalize_event_cta_flyer requires such a flyer to declare no amount.
 * The insurance artwork is chosen BY EVENT TYPE (owner 2026-10-12: design delivered one for insurance RANGE NIGHTS and
 * one for insurance GUN SHOWS). INSURANCE_ARTWORK_BY_EVENT_TYPE maps a type to its artwork; a type with no entry (any
 * other insurance-state event type) gets NO flyer -- the button is disabled with a reason ("artwork_pending"), nothing is
 * rendered or saved, and no automatic flyer is made. To add a type: put design's file under public/flyers/, add its
 * entry to flyerArtworks.json (file, size, DPI, measured QR box) and to FLYER_ARTWORKS, one line in
 * INSURANCE_ARTWORK_BY_EVENT_TYPE, and its sha256 in scripts/check-flyer-artwork.mjs (which then requires that file).
 * Round 6 (external audit fca9b75d P2): the server names the artwork too (render snapshot `flyer_artwork`, from the
 * event's type in the same statement) and finalize checks the snapshot's `flyer_fingerprint`, which folds it in -- so
 * a flyer rendered for a gun show is refused once the event is a range night, and Repair finds a saved flyer whose
 * event type changed afterwards.
 *
 * The QR encodes the CTA's CURRENT URL (audit H1, 2ee88b30): the CTA row is re-read, and if its saved
 * `generated_url` is not what `buildCTAUrl` makes from the row today (an edit since the QR was made),
 * the CTA's QR is regenerated and saved FIRST, then the flyer uses that. If regeneration fails, no
 * flyer is made and the previous one is untouched. EXCEPT on an event created before the go-forward
 * cutoff (external audit P2, 35406751): there Create flyer only renders, so a stale saved URL is refused
 * (LEGACY_STALE_URL_REASON) and the URL and QR are left exactly as they are.
 *
 * Identity and replacement are the SERVER's (audit M1): after the normal reserve/upload/confirm,
 * `finalize_event_cta_flyer` ties the attachment to the CTA by full id and, under a per-CTA lock,
 * retires every other live flyer of that CTA in one transaction (a unique index allows one). The file
 * name is only a human label.
 */
import QRCode from "qrcode";

import {
  deleteEventAttachment,
  uploadEventAttachment,
  EVENT_ATTACHMENTS_BUCKET,
  type EventAttachment,
} from "./eventAttachments.service";
import {
  ctaOmitsDiscount,
  toBaseVariant,
  type CTABaseVariant,
} from "./ctaClassroom";
import { buildCTAUrl, generateCTAQRCode } from "./eventCTA.service";
import { deleteFile } from "./storage.service";
import { supabase } from "./supabase";
import type { Event, EventCallToAction } from "../types/event.types";
import { eventTypeIsNoPromo } from "../utils/eventTypePolicy";

/**
 * EVERY artwork the app can render is an entry of the data-only manifest flyerArtworks.json (external audit fca9b75d
 * P3): file (under public/, same origin so the canvas stays exportable), native size, print DPI and the QR square in
 * native pixels. Nothing else names an artwork: FLYER_ARTWORKS below is built from it, renderFlyerPng refuses any
 * artwork that is not one of its entries, and scripts/check-flyer-artwork.mjs validates every entry (reviewed file,
 * sha256-pinned) AND the resolved FLYER_ARTWORKS / INSURANCE_ARTWORK_BY_EVENT_TYPE values before a build.
 *
 * Measurements (native pixels):
 *  - standard / standardNoAmount (2550x3300 @ 300 DPI, measured 2026-09-29): the white QR box x 824..1722, y 1965..2864
 *    (899x900, every pixel inside pure white). The "$20"-free copy is made by scripts/flyer/make_blank_amount_template.py.
 *  - insuranceRangeNight (3060x3960 @ 360 DPI, design 2026-10-12 "26-07_Member-Event_Table_Sign_8.5x11_Sign-Up_GSW_02",
 *    "GET A GUNSHOT WOUND KIT + METAL CARD", sha256 c9ab8c41...): pure-white region x 990..2067, y 2256..3332
 *    (1078x1077); the QR is the 1077-px square at (990, 2256).
 *  - insuranceGunShow (3060x3960 @ 360 DPI, design v2 2026-10-13 "26-06_Gun_Show_Voucher_Table-Signs_8.5x11_Check-In_GSW 1",
 *    served as qr-join-insurance-gun-show-blank-v2.png -- a NEW path, because /flyers/ is cached immutable),
 *    "GET FREE ADMISSION + A GUNSHOT WOUND KIT", no state footer code, sha256 5f756cd4...): no drawn box; rows
 *    2388..3534 are all white between "Scan Here to Join Today!" and the navy line (y 3535..3545, x 1066..1994). The QR
 *    is 900 px (2.5 in, the same 0.294 of the page width as the v1 file's 800/2720), centred on the page (x 1080..1979)
 *    and in the gap (y 2511..3410): 123 px of white above, 124 below, plus the QR's own quiet zone.
 */
import FLYER_ARTWORK_MANIFEST from "./flyerArtworks.json";

const FLYER_FONT_URL = "/fonts/InterTight-Variable.ttf"; // OFL, see InterTight-OFL.txt
const FLYER_FONT_FAMILY = "Flyer Inter Tight";

/** The QR square as FRACTIONS of the artwork's own size. */
export type FlyerBox = { left: number; top: number; size: number };
/**
 * An artwork, its native pixel size and print resolution, and its OWN QR box (fractions of its own size). The flyer is
 * rendered at the artwork's native size (drawn 1:1, never resampled) and the QR is drawn natively at that size, so
 * both stay crisp; the PNG is stamped with the artwork's DPI so it prints at 8.5x11 in.
 */
export type FlyerArtwork = {
  /**
   * The server's artwork key for this design (migration 2026-10-14-470): what _eib_cta_flyer_artwork requires and what
   * finalize_event_cta_flyer records. A REPLACED design gets a new versioned key (insurance:gun_show:v2), so a tab still
   * holding the older design (old JS) is refused by the server instead of saving it under the new design's name.
   */
  key: string;
  url: string;
  width: number;
  height: number;
  dpi: number;
  box: FlyerBox;
  /**
   * Mig 1480 generic designs only: where the browser draws the event's amount DIGITS (the design keeps its own "GET" and
   * superscript "$"; its digits were removed). Native pixels of this artwork.
   */
  amountSlot?: AmountSlot;
};

/** Native-pixel geometry of a generic design's amount digits (mig 1480). */
export type AmountSlot = {
  capTop: number;
  baseline: number;
  left: number;
  right: number;
  color: string;
};

type ManifestEntry = {
  key: string;
  file: string;
  width: number;
  height: number;
  dpi: number;
  box: { x: number; y: number; size: number };
  amount?: AmountSlot;
};
export type FlyerArtworkId = keyof typeof FLYER_ARTWORK_MANIFEST;

function manifestArtwork(e: ManifestEntry): FlyerArtwork {
  return {
    key: e.key,
    url: `/${e.file}`,
    width: e.width,
    height: e.height,
    dpi: e.dpi,
    box: {
      left: e.box.x / e.width,
      top: e.box.y / e.height,
      size: e.box.size / e.width,
    },
    ...(e.amount ? { amountSlot: { ...e.amount } } : {}),
  };
}

/** Every artwork the app can render -- exactly the manifest's entries. */
export const FLYER_ARTWORKS: Readonly<Record<FlyerArtworkId, FlyerArtwork>> =
  Object.freeze({
    standard: manifestArtwork(FLYER_ARTWORK_MANIFEST.standard),
    standardNoAmount: manifestArtwork(FLYER_ARTWORK_MANIFEST.standardNoAmount),
    insuranceRangeNight: manifestArtwork(
      FLYER_ARTWORK_MANIFEST.insuranceRangeNight,
    ),
    insuranceGunShow: manifestArtwork(FLYER_ARTWORK_MANIFEST.insuranceGunShow),
    eibRn100: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibRn100),
    eibRn50: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibRn50),
    eibRn75: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibRn75),
    eibRn99: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibRn99),
    eibRn15Gsw: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibRn15Gsw),
    eibRnGsw: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibRnGsw),
    eibGs20ThirtyDay: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibGs20ThirtyDay),
    eibGs20: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibGs20),
    eibGs15: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibGs15),
    eibGsGsw: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibGsGsw),
    eibRnGeneric: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibRnGeneric),
    eibRnGenericGsw: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibRnGenericGsw),
    eibGsGeneric: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibGsGeneric),
    eibGsGenericThirtyDay: manifestArtwork(
      FLYER_ARTWORK_MANIFEST.eibGsGenericThirtyDay,
    ),
    eibGsGenericGsw: manifestArtwork(FLYER_ARTWORK_MANIFEST.eibGsGenericGsw),
  });

/**
 * True only for an artwork that is EXACTLY a manifest entry (file, size, DPI and box) -- the only kind renderFlyerPng
 * draws. Compared by value against the manifest itself, not against FLYER_ARTWORKS, so an edited FLYER_ARTWORKS entry
 * cannot vouch for itself.
 */
export function isManifestArtwork(a: FlyerArtwork): boolean {
  return (Object.values(FLYER_ARTWORK_MANIFEST) as ManifestEntry[]).some(
    (e) => {
      const m = manifestArtwork(e);
      return (
        m.key === a.key &&
        m.url === a.url &&
        m.width === a.width &&
        m.height === a.height &&
        m.dpi === a.dpi &&
        m.box.left === a.box.left &&
        m.box.top === a.box.top &&
        m.box.size === a.box.size &&
        JSON.stringify(m.amountSlot ?? null) ===
          JSON.stringify(a.amountSlot ?? null)
      );
    },
  );
}

/** Served from public/. Kept as named exports for callers and tests. */
export const FLYER_TEMPLATE_URL = FLYER_ARTWORKS.standard.url;
export const FLYER_TEMPLATE_NO_AMOUNT_URL = FLYER_ARTWORKS.standardNoAmount.url;
export const FLYER_TEMPLATE_INSURANCE_RANGE_NIGHT_URL =
  FLYER_ARTWORKS.insuranceRangeNight.url;
export const FLYER_TEMPLATE_INSURANCE_GUN_SHOW_URL =
  FLYER_ARTWORKS.insuranceGunShow.url;

/**
 * Insurance-state flyer artwork BY EVENT TYPE (owner 2026-10-12: range nights and gun shows each have design's own
 * file). A type that is not listed (any other insurance-state event type) has no insurance artwork: its CTAs get no
 * flyer ("artwork_pending"). To add one: a flyerArtworks.json + FLYER_ARTWORKS entry (file + measured box) and one line here.
 */
export const INSURANCE_ARTWORK_BY_EVENT_TYPE: Readonly<
  Record<string, FlyerArtwork | undefined>
> = {
  range_night: FLYER_ARTWORKS.insuranceRangeNight,
  gun_show: FLYER_ARTWORKS.insuranceGunShow,
};

/**
 * The artwork key the database's _eib_cta_flyer_artwork names for this content (migration 410 round 6; versioned by
 * migration 2026-10-14-470): the key of the manifest entry this browser would DRAW -- 'standard' (both standard files;
 * the amount is already part of the URL fingerprint), 'insurance:range_night', 'insurance:gun_show:v2'. An insurance type
 * with no artwork has no entry; it keeps the server's unversioned form 'insurance:<event type>' (nothing is drawn for it).
 */
export function flyerArtworkKey(content: FlyerContent): string {
  if (content.kind === "insurance") {
    return (
      insuranceArtworkFor(content.eventType)?.key ??
      `insurance:${content.eventType ?? ""}`
    );
  }
  if (content.kind === "server") {
    return content.key;
  }
  return flyerArtworkFor(content).artwork.key;
}

/** The insurance artwork for this event type, or null when design has not delivered one. */
export function insuranceArtworkFor(
  eventType: string | null | undefined,
): FlyerArtwork | null {
  if (
    !eventType ||
    !Object.prototype.hasOwnProperty.call(
      INSURANCE_ARTWORK_BY_EVENT_TYPE,
      eventType,
    )
  ) {
    return null;
  }
  return INSURANCE_ARTWORK_BY_EVENT_TYPE[eventType] ?? null;
}

const EVENT_TYPE_LABEL: Record<string, string> = {
  gun_show: "gun show",
  range_night: "range night",
};

/** Why an insurance-state CTA has no flyer yet (its event type has no insurance artwork). */
export function insuranceArtworkPendingReason(
  eventType: string | null | undefined,
): string {
  const label =
    // Own keys only (internal audit P3: "toString" would otherwise label from Object.prototype).
    (eventType &&
      Object.prototype.hasOwnProperty.call(EVENT_TYPE_LABEL, eventType) &&
      EVENT_TYPE_LABEL[eventType]) ||
    (eventType ? eventType.replace(/_/g, " ") : "this kind of event");
  return `There is no insurance-state flyer design for a ${label} yet (only range nights and gun shows have one), so no flyer can be made for this event. Its link and QR code work as usual.`;
}

/** Thrown when an insurance-state flyer is asked for an event type with no insurance artwork. Nothing is saved. */
export class FlyerArtworkPendingError extends Error {
  readonly eventType: string | null | undefined;
  constructor(eventType: string | null | undefined, message?: string) {
    super(message ?? insuranceArtworkPendingReason(eventType));
    this.name = "FlyerArtworkPendingError";
    this.eventType = eventType;
  }
}

/**
 * What a flyer shows: an insurance-state flyer (no amount; artwork by event type), or a standard flyer with the CTA's
 * amount.
 */
export type FlyerContent =
  | { kind: "insurance"; eventType: string | null | undefined }
  | { kind: "amount"; amount: number }
  /**
   * Mig 1480: the design the SERVER names for a gun show / range night with a state offer (eib:...). `amount` is drawn
   * only on a generic design (one with an amountSlot); a state design prints its own amount.
   */
  | { kind: "server"; key: string; amount: number | null };

/** Why the server's design key has no flyer (mig 1480). */
export function serverArtworkPendingReason(key: string): string {
  if (key === "eib:none") {
    return "This state has no Event in a Box offer (Washington and Massachusetts, where U.S. LawShield sends sign-ups to its legacy page, and Alaska, Maine, New York and Rhode Island, where it is not operating), so no flyer is made. The event's link and QR code work as usual.";
  }
  return `This event needs the flyer design "${key}", which this page doesn't have. Reload the page (Cmd+Shift+R on a Mac, Ctrl+Shift+R on Windows) and try again.`;
}

/** The manifest artwork with this server key (the 'standard' key is handled by the amount rule, not here). */
export function artworkForServerKey(key: string): FlyerArtwork | null {
  if (key === "standard") {
    return null;
  }
  const hit = (Object.values(FLYER_ARTWORKS) as FlyerArtwork[]).find(
    (a) => a.key === key,
  );
  return hit ?? null;
}

/**
 * The artwork and the amount to draw (null = draw none). An insurance-state flyer never draws an amount; one whose event
 * type has no insurance artwork throws FlyerArtworkPendingError (never a placeholder, never the $20 artwork).
 */
export function flyerArtworkFor(content: FlyerContent): {
  artwork: FlyerArtwork;
  drawAmount: number | null;
} {
  if (content.kind === "insurance") {
    const artwork = insuranceArtworkFor(content.eventType);
    if (!artwork) {
      throw new FlyerArtworkPendingError(content.eventType);
    }
    return { artwork, drawAmount: null };
  }
  if (content.kind === "server") {
    if (content.key === "standard") {
      return flyerArtworkFor({
        kind: "amount",
        amount: Number(content.amount),
      });
    }
    const artwork = artworkForServerKey(content.key);
    if (!artwork) {
      throw new FlyerArtworkPendingError(
        null,
        serverArtworkPendingReason(content.key),
      );
    }
    if (artwork.amountSlot) {
      if (
        content.amount === null ||
        !Number.isFinite(content.amount) ||
        content.amount <= 0
      ) {
        throw new Error(
          "This flyer design shows the CTA's amount, but the CTA gives no voucher.",
        );
      }
      return { artwork, drawAmount: content.amount };
    }
    return { artwork, drawAmount: null };
  }
  return content.amount === ARTWORK_AMOUNT
    ? { artwork: FLYER_ARTWORKS.standard, drawAmount: null }
    : { artwork: FLYER_ARTWORKS.standardNoAmount, drawAmount: content.amount };
}

/**
 * Go-forward only (owner, 2026-09-26): flyers exist for events CREATED from this instant on. Older
 * events never get one automatically. Kept as a constant, not a setting, so the boundary cannot drift.
 *
 * One exception, by button only (owner, 2026-10-12): an older event whose GENERIC CTA is already a
 * complete current-rule CTA — see `isCompleteCurrentRuleGenericCTA`. The cutoff existed because older
 * events' CTAs were never prepared under the current rules (most generic CTAs carried no voucher and no
 * store codes, so a flyer would advertise a credit the QR never issues or send signups unattributed).
 * The 2026-10-12 data repair made 15 such range-night CTAs complete; for exactly those, the reason for the
 * cutoff no longer holds, and sales would otherwise rebuild the PDF by hand.
 */
export const FLYER_EVENTS_CREATED_FROM = "2026-09-27T00:00:00Z";

/** Created before the go-forward cutoff. A missing or unparseable created_at counts as before. */
export function isPreCutoffEvent(event: Pick<Event, "created_at">): boolean {
  return (
    !event.created_at ||
    !(
      new Date(event.created_at).getTime() >=
      Date.parse(FLYER_EVENTS_CREATED_FROM)
    )
  );
}

/**
 * External audit P2 (35406751): on an older event "Create flyer" only RENDERS. If the CTA's saved URL is not
 * the URL the app builds from the CTA today (e.g. its promo was edited after the QR was made), the ordinary
 * post-cutoff path would regenerate the saved URL and QR first -- on an older event that silently rewrites a
 * link that may already be printed. So it refuses instead, and leaves the URL and QR untouched.
 */
export const LEGACY_STALE_URL_REASON =
  "This event's QR link is out of date: the CTA changed after its QR code was made. Because this event was created before 9/27/2026, Create flyer won't regenerate it. Use \"Regenerate URL & QR\" first (or ask an admin to), then create the flyer. The saved link and QR code were not changed.";

/** Fields `buildCTAUrl` reads; optional so the pure eligibility helpers accept narrow test shapes. */
type FlyerCTA = Pick<
  EventCallToAction,
  | "id"
  | "cta_type"
  | "reward_type"
  | "voucher_amount"
  | "generated_url"
  | "is_system_generic"
  | "parameters"
> &
  Partial<
    Pick<
      EventCallToAction,
      | "base_url"
      | "include_state_param"
      | "use_state_defaults"
      | "single_voucher"
      | "use_member_signup"
      | "display_away_plans"
    >
  >;

/**
 * The URL the app builds for this CTA today -- exactly what `generateCTAQRCode` would save: `buildCTAUrl` on the
 * same row values, unnormalised (internal audit P3-1: a normalised comparison could disagree with what the
 * refresh writes). The snapshot RPC and PostgREST both deliver `voucher_amount` as a JSON number.
 */
export function canonicalCTAUrl(
  cta: FlyerCTA,
  event: Pick<Event, "id"> & { state?: string | null },
  /**
   * Migration 410: the event's variant from the database. REQUIRED (not optional) so no caller can silently build the
   * standard form for an insurance-state CTA -- that form carries discountvalue and would never equal the saved link.
   */
  baseVariant: CTABaseVariant,
): string {
  return buildCTAUrl(
    {
      ...cta,
      base_url: cta.base_url ?? "",
      parameters: cta.parameters ?? {},
    },
    {
      id: event.id,
      state: event.state ?? undefined,
      cta_base_variant: baseVariant,
    },
  );
}

/**
 * A generic CTA that already looks like one prepared under the current rules: the system generic CTA, a
 * subscription CTA with a positive voucher, the store codes present (affid AND promo), and a saved URL that
 * carries THIS CTA's id and THIS event's id (what `buildCTAUrl` writes today — the URL the QR encodes).
 * It is the gate that lets a pre-cutoff event's "Create flyer" button show; it does not change anything.
 * Showing is not enabling: `flyerEligibility` also requires the saved URL to EQUAL `canonicalCTAUrl` on a
 * pre-cutoff event (kind "legacy_stale_url" otherwise), and the save path re-checks that on the server
 * snapshot before its only QR-write branch.
 */
export function isCompleteCurrentRuleGenericCTA(
  cta: Pick<
    EventCallToAction,
    | "id"
    | "cta_type"
    | "reward_type"
    | "voucher_amount"
    | "generated_url"
    | "is_system_generic"
    | "parameters"
  >,
  event: Pick<Event, "id">,
): boolean {
  if (cta.is_system_generic !== true || cta.cta_type !== "subscription") {
    return false;
  }
  const amount = Number(cta.voucher_amount);
  if (
    cta.reward_type !== "voucher" ||
    cta.voucher_amount === null ||
    cta.voucher_amount === undefined ||
    !Number.isFinite(amount) ||
    amount <= 0
  ) {
    return false;
  }
  const filled = (key: string) =>
    typeof cta.parameters?.[key] === "string" &&
    cta.parameters[key].trim() !== "";
  if (!filled("affid") || !filled("promo")) {
    return false;
  }
  if (!cta.generated_url || !cta.id || !event.id) {
    return false;
  }
  let url: URL;
  try {
    url = new URL(cta.generated_url);
  } catch {
    return false;
  }
  return (
    url.searchParams.get("ctaid") === cta.id &&
    url.searchParams.get("eventid") === event.id
  );
}

/** The amount printed in the original artwork; a CTA with exactly this amount uses it untouched. */
const ARTWORK_AMOUNT = 20;

/**
 * Where the amount goes, in 816-px-wide reference units (measured 2026-09-26 on the 816x1056 artwork;
 * re-measured 2026-09-29 on the 2550x3300 artwork: "$" and "20" ink within 1.2 reference px, and
 * "INSTANT CREDIT" still ends at x=734, so the values stand). The "$" is a
 * superscript: its ink box is 90 px tall with its top 11 px above the digits' cap line. The amount
 * is LEFT-aligned where the original "$" starts, so the gap after "GET" never changes; if it would
 * run past the right edge of the line below ("INSTANT CREDIT", x=734) the whole amount shrinks,
 * keeping its baseline.
 */
const AMOUNT = {
  refWidth: 816,
  capTop: 287,
  baseline: 404,
  dollarTop: 276,
  dollarInkHeight: 90,
  gap: 5,
  left: 458,
  right: 734,
  digitPx: 162, // font size whose cap height is the artwork's 118 px
  tracking: -3,
  color: "#FFFCF7", // sampled from the artwork's lettering
};

/** "$28", or "$19.99" when there are cents. */
export function formatFlyerAmount(amount: number): string {
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

/**
 * `kind` drives the UI: "predates", "ended" and "not_subscription" HIDE the button (a flyer doesn't apply);
 * "amount", "no_qr" and "legacy_stale_url" show it disabled with the reason, because the operator (or an
 * admin) can fix those. "artwork_pending" (an insurance-state event type with no insurance artwork: not a range night or gun show)
 * also shows it disabled with the reason, so the operator knows the flyer is coming rather than missing.
 */
export type FlyerEligibility =
  | { ok: true }
  | {
      ok: false;
      kind:
        | "predates"
        | "ended"
        | "not_subscription"
        | "amount"
        | "no_qr"
        | "legacy_stale_url"
        | "artwork_pending"
        | "no_promo_type";
      reason: string;
    };

/**
 * One signature for both branches (merge of feature/flyer-cta-picker-20 into the insurance branch, 2026-10-12):
 *   `opts.mode`: "automatic" (the default — flyers made with no click, after preparation or a link save) keeps the
 *     cutoff absolute. "button" (an operator's "Create flyer" click) also admits a pre-cutoff event whose CTA is a
 *     complete current-rule generic CTA (owner, 2026-10-12).
 *   `opts.baseVariant`: the event's variant from the database (render snapshot / event_cta_base_variant, migration
 *     410). An insurance-state CTA's flyer shows no amount, so it is NOT refused for lacking a voucher -- but it needs
 *     an insurance artwork for the event's TYPE (`event.type`), or it is "artwork_pending". Absent = standard.
 * An options object, not positional arguments, so neither branch's extra argument can land in the other's slot.
 */
export function flyerEligibility(
  cta: FlyerCTA,
  event: Pick<Event, "id" | "end_time" | "created_at"> & {
    state?: string | null;
    type?: string | null;
  },
  opts: {
    mode?: "automatic" | "button";
    baseVariant?: CTABaseVariant | null;
  } = {},
): FlyerEligibility {
  const mode = opts.mode ?? "automatic";
  const baseVariant = toBaseVariant(opts.baseVariant);
  // Owner 2026-10-29: a class, table, seminar or speaking engagement gets no flyer (it has no event CTA; it uses the
  // venue's QR codes). Checked first, in both modes.
  if (eventTypeIsNoPromo(event.type)) {
    return {
      ok: false,
      kind: "no_promo_type",
      reason:
        "Classes, tables, seminars and speaking engagements have no event flyer; they use the venue's QR codes.",
    };
  }
  const predates = isPreCutoffEvent(event);
  if (
    predates &&
    !(mode === "button" && isCompleteCurrentRuleGenericCTA(cta, event))
  ) {
    return {
      ok: false,
      kind: "predates",
      reason: "Flyers are only made for events created from 9/27/2026 on.",
    };
  }
  if (event.end_time && new Date(event.end_time).getTime() < Date.now()) {
    return { ok: false, kind: "ended", reason: "This event has ended." };
  }
  if (cta.cta_type !== "subscription") {
    return {
      ok: false,
      kind: "not_subscription",
      reason: "Flyers are only for subscription CTAs.",
    };
  }
  const noAmount = ctaOmitsDiscount(cta, baseVariant);
  // Owner 2026-10-12: an insurance-state flyer uses the artwork for the event's type; a type design has not delivered
  // (anything but a range night or gun show) gets none -- never a placeholder, never the $20 artwork.
  if (noAmount && !insuranceArtworkFor(event.type)) {
    return {
      ok: false,
      kind: "artwork_pending",
      reason: insuranceArtworkPendingReason(event.type),
    };
  }
  const amount = Number(cta.voucher_amount);
  if (
    !noAmount &&
    (cta.reward_type !== "voucher" ||
      cta.voucher_amount === null ||
      cta.voucher_amount === undefined ||
      !Number.isFinite(amount) ||
      amount <= 0)
  ) {
    return {
      ok: false,
      kind: "amount",
      reason:
        "The flyer advertises an instant credit, but this CTA gives no voucher.",
    };
  }
  if (!cta.generated_url) {
    return {
      ok: false,
      kind: "no_qr",
      reason: "Generate this CTA's QR code first.",
    };
  }
  // External audit P2 (35406751): an older event is admitted for RENDERING only. Its saved URL must be
  // exactly the URL the app would build now, or the save path would have to regenerate the QR.
  if (
    predates &&
    cta.generated_url !== canonicalCTAUrl(cta, event, baseVariant)
  ) {
    return {
      ok: false,
      kind: "legacy_stale_url",
      reason: LEGACY_STALE_URL_REASON,
    };
  }
  return { ok: true };
}

/**
 * One flyer per CTA: the name carries the CTA id, and re-creating replaces the file with this exact
 * name. Never matched by pattern — exact equality only.
 */
export function flyerFileName(
  cta: Pick<EventCallToAction, "id">,
  label: string,
): string {
  const clean = label
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60);
  return `QR flyer - ${clean || "CTA"} - ${cta.id.slice(0, 8)}.png`;
}

/** Operator-facing label for the file name: the rep, or "Generic". */
export function flyerLabel(
  cta: Pick<EventCallToAction, "is_system_generic" | "name"> & {
    sales_position?: {
      assigned_name?: string | null;
      title?: string | null;
    } | null;
  },
): string {
  if (cta.sales_position) {
    return (
      cta.sales_position.assigned_name || cta.sales_position.title || cta.name
    );
  }
  return cta.is_system_generic ? "Generic" : cta.name;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Couldn't load the flyer template."));
    img.src = src;
  });
}

let flyerFont: Promise<void> | null = null;
function loadFlyerFont(): Promise<void> {
  if (!flyerFont) {
    const face = new FontFace(FLYER_FONT_FAMILY, `url(${FLYER_FONT_URL})`, {
      weight: "100 900",
    });
    flyerFont = face
      .load()
      .then((loaded) => {
        document.fonts.add(loaded);
      })
      .catch((err) => {
        flyerFont = null; // allow a retry
        throw err;
      });
  }
  return flyerFont;
}

/** Draws "$<amount>" onto the amount-free artwork. `k` = output px per reference px. */
function drawAmountOn(
  ctx: CanvasRenderingContext2D,
  amount: number,
  k: number,
) {
  const text = formatFlyerAmount(amount);
  const measure = (px: number, s: string) => {
    ctx.font = `900 ${px}px "${FLYER_FONT_FAMILY}"`;
    return ctx.measureText(s);
  };
  const probe = measure(100, "$");
  const dollarPxPerInk =
    100 / (probe.actualBoundingBoxAscent + probe.actualBoundingBoxDescent);

  const layout = (scale: number) => {
    const digitPx = AMOUNT.digitPx * k * scale;
    const dollarPx = AMOUNT.dollarInkHeight * k * scale * dollarPxPerInk;
    const d = measure(dollarPx, "$");
    const dollarInk = d.actualBoundingBoxLeft + d.actualBoundingBoxRight;
    let x = 0;
    let minX = Infinity;
    let maxX = -Infinity;
    const pos: number[] = [];
    for (const ch of text) {
      const m = measure(digitPx, ch);
      pos.push(x);
      minX = Math.min(minX, x - m.actualBoundingBoxLeft);
      maxX = Math.max(maxX, x + m.actualBoundingBoxRight);
      x += m.width + AMOUNT.tracking * k * scale;
    }
    const total = dollarInk + AMOUNT.gap * k * scale + (maxX - minX);
    return { digitPx, dollarPx, d, dollarInk, pos, minX, total, scale };
  };

  const maxWidth = (AMOUNT.right - AMOUNT.left) * k;
  let L = layout(1);
  if (L.total > maxWidth) {
    L = layout(maxWidth / L.total);
  }

  ctx.fillStyle = AMOUNT.color;
  ctx.textBaseline = "alphabetic";
  const left = AMOUNT.left * k;
  const baseline = AMOUNT.baseline * k;
  const digitTop = baseline - (AMOUNT.baseline - AMOUNT.capTop) * k * L.scale;
  const dollarTop = digitTop - (AMOUNT.capTop - AMOUNT.dollarTop) * k * L.scale;

  ctx.font = `900 ${L.dollarPx}px "${FLYER_FONT_FAMILY}"`;
  ctx.fillText(
    "$",
    left + L.d.actualBoundingBoxLeft,
    dollarTop + L.d.actualBoundingBoxAscent,
  );
  ctx.font = `900 ${L.digitPx}px "${FLYER_FONT_FAMILY}"`;
  const digitsX = left + L.dollarInk + AMOUNT.gap * k * L.scale - L.minX;
  [...text].forEach((ch, i) => ctx.fillText(ch, digitsX + L.pos[i], baseline));
}

/**
 * Mig 1480 generic designs: draws the amount's DIGITS (the design keeps its own "GET" and "$") left-aligned at the
 * slot's left edge, at the design's digit cap height and baseline, in Inter Tight Black; an amount wider than the slot
 * shrinks, keeping its baseline. Same font and colour as the standard flyer's drawn amount.
 */
function drawDigitsOn(
  ctx: CanvasRenderingContext2D,
  amount: number,
  slot: AmountSlot,
) {
  const text = formatFlyerAmount(amount);
  const fontAt = (px: number) => `900 ${px}px "${FLYER_FONT_FAMILY}"`;
  ctx.font = fontAt(200);
  const capProbe = ctx.measureText("8").actualBoundingBoxAscent;
  let px = (200 * (slot.baseline - slot.capTop)) / capProbe;
  const span = (size: number) => {
    ctx.font = fontAt(size);
    const tracking = -size * 0.01;
    let x = 0;
    const first = ctx.measureText(text[0]);
    const minX = -first.actualBoundingBoxLeft;
    let maxX = 0;
    const pos: number[] = [];
    for (const ch of text) {
      const m = ctx.measureText(ch);
      pos.push(x);
      maxX = x + m.actualBoundingBoxRight;
      x += m.width + tracking;
    }
    return { minX, width: maxX - minX, pos };
  };
  let L = span(px);
  const avail = slot.right - slot.left;
  if (L.width > avail) {
    px = (px * avail) / L.width;
    L = span(px);
  }
  ctx.font = fontAt(px);
  ctx.fillStyle = slot.color;
  ctx.textBaseline = "alphabetic";
  const x0 = slot.left - L.minX;
  [...text].forEach((ch, i) => ctx.fillText(ch, x0 + L.pos[i], slot.baseline));
}

/**
 * Renders the flyer as a US-Letter PNG at the ARTWORK's native size (standard: 2550x3300 @ 300 DPI; insurance range
 * night: 3060x3960 @ 360 DPI; insurance gun show: 3060x3960 @ 360 DPI): the insurance-state flyer (no amount), or one
 * showing `amount`.
 */
export async function renderFlyerPng(
  qrUrl: string,
  content: FlyerContent,
): Promise<Blob> {
  const { artwork, drawAmount } = flyerArtworkFor(content);
  if (!isManifestArtwork(artwork)) {
    // External audit fca9b75d P3: only an artwork from flyerArtworks.json (validated before every build) is drawn.
    throw new Error(
      `The flyer artwork ${artwork.url} is not a reviewed artwork; no flyer was made.`,
    );
  }
  const [template] = await Promise.all([
    loadImage(artwork.url),
    drawAmount === null ? Promise.resolve() : loadFlyerFont(),
  ]);

  const outW = artwork.width;
  const outH = artwork.height;
  // The template the browser actually loaded must be the manifest's size. /flyers/ is served immutable for a year, so
  // a cached older design at the same path would otherwise be scaled onto this canvas without any error.
  if (template.naturalWidth !== outW || template.naturalHeight !== outH) {
    throw new Error(
      `The flyer artwork ${artwork.url} loaded at ${template.naturalWidth}x${template.naturalHeight}, not ${outW}x${outH}; no flyer was made. Reload the page and try again.`,
    );
  }
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("This browser can't render the flyer.");
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // Drawn 1:1 (the canvas is the artwork's own size), so the artwork is never resampled.
  ctx.drawImage(template, 0, 0, outW, outH);
  if (drawAmount !== null && artwork.amountSlot) {
    drawDigitsOn(ctx, drawAmount, artwork.amountSlot);
  } else if (drawAmount !== null) {
    drawAmountOn(ctx, drawAmount, outW / AMOUNT.refWidth);
  }

  // The box in output pixels. Its own white fill is the background; the QR's quiet zone (margin)
  // keeps the code off the box edge.
  const boxX = Math.round(artwork.box.left * outW);
  const boxY = Math.round(artwork.box.top * outH);
  const boxSize = Math.round(artwork.box.size * outW);

  const qrCanvas = document.createElement("canvas");
  await QRCode.toCanvas(qrCanvas, qrUrl, {
    errorCorrectionLevel: "M",
    margin: 2,
    width: boxSize,
    color: { dark: "#000000", light: "#ffffff" },
  });
  // Draw at native size, centred (the library rounds to whole pixels per module). No smoothing, so
  // module edges stay sharp.
  ctx.imageSmoothingEnabled = false;
  const qrSize = Math.min(qrCanvas.width, boxSize);
  ctx.drawImage(
    qrCanvas,
    boxX + Math.floor((boxSize - qrSize) / 2),
    boxY + Math.floor((boxSize - qrSize) / 2),
    qrSize,
    qrSize,
  );

  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Couldn't export the flyer.")),
      "image/png",
    ),
  );
}

/**
 * Renders and saves the flyer to the event's Files, then removes this CTA's PREVIOUS flyer.
 * Upload first, delete second: a failed upload never loses the flyer that was already there.
 */
/**
 * A readable message from anything thrown: Supabase/PostgREST errors are PLAIN OBJECTS with a
 * `message`, not Error instances (audit L1 -- `String(err)` gave "[object Object]").
 */
export function errorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (
    err &&
    typeof err === "object" &&
    typeof (err as { message?: unknown }).message === "string"
  ) {
    return (err as { message: string }).message;
  }
  return String(err);
}

/** Thrown when the user cannot save to this event's Files -- retrying cannot help (audit M2). */
export class FlyerFilesAccessError extends Error {
  constructor() {
    super(
      "You don't have access to this event's Files, so the flyer can't be saved. Someone who does can use \"Create flyer\" on the CTA — an admin or manager, or a teammate this event is shared with.",
    );
    this.name = "FlyerFilesAccessError";
  }
}

/** Files access for this event: the same predicate the attachment RPCs enforce. */
export async function canManageEventFiles(eventId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("event_attachment_can_manage", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return data === true;
}

export type CTAFlyerState = "saved" | "missing" | "removed" | "no_access";

/**
 * Whether this CTA has a live flyer in the event's Files. The link (`source_cta_id`) is set only when
 * a flyer is finalized, so it is the authority -- not the file name. "removed" = it had one and
 * someone deleted it (not re-made automatically). Someone without Files access cannot see or make
 * flyers: "no_access".
 */
export async function getCTAFlyerState(
  eventId: string,
  ctaId: string,
): Promise<CTAFlyerState> {
  if (!(await canManageEventFiles(eventId))) {
    return "no_access";
  }
  const { data, error } = await supabase
    .from("event_attachments")
    .select("id, deleted_at")
    .eq("event_id", eventId)
    .eq("source_cta_id", ctaId)
    .order("deleted_at", { ascending: false, nullsFirst: true })
    .limit(1);
  if (error) {
    throw error;
  }
  const row = (data ?? [])[0];
  if (!row) {
    return "missing";
  }
  return row.deleted_at ? "removed" : "saved";
}

/**
 * Embeds physical-size metadata (a PNG `pHYs` chunk) so the file prints at 8.5x11 in by default (2550x3300 at 300
 * DPI; insurance range night 3060x3960 at 360 DPI; insurance gun show 3060x3960 at 360 DPI). Canvas export writes no pHYs; without it, print size was left to the viewer (audit).
 */
export async function withPngDpi(blob: Blob, dpi: number): Promise<Blob> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const ppm = Math.round(dpi / 0.0254);
  const chunk = new Uint8Array(21); // length(4) + "pHYs"(4) + data(9) + crc(4)
  const view = new DataView(chunk.buffer);
  view.setUint32(0, 9);
  chunk.set([0x70, 0x48, 0x59, 0x73], 4); // "pHYs"
  view.setUint32(8, ppm);
  view.setUint32(12, ppm);
  chunk[16] = 1; // unit: metre
  view.setUint32(17, crc32(chunk.subarray(4, 17)));
  // Signature (8) + IHDR chunk (4 + 4 + 13 + 4 = 25) -> pHYs goes right after IHDR, before IDAT.
  const at = 33;
  const out = new Uint8Array(bytes.length + chunk.length);
  out.set(bytes.subarray(0, at), 0);
  out.set(chunk, at);
  out.set(bytes.subarray(at), at + chunk.length);
  return new Blob([out], { type: "image/png" });
}

function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    c ^= data[i];
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? (c >>> 1) ^ 0xedb88320 : c >>> 1;
    }
  }
  return (c ^ 0xffffffff) >>> 0;
}

/**
 * Renders the flyer from the CTA's CURRENT state and saves it to the event's Files, replacing that
 * CTA's previous flyer atomically on the server. Order: re-read CTA -> refresh a stale QR -> render ->
 * upload (reserve/upload/confirm) -> finalize (claim + retire old, one transaction) -> remove retired
 * bytes. Nothing before the finalize touches the previous flyer.
 */
export async function saveCTAFlyerToEvent(params: {
  cta: EventCallToAction;
  event: Event;
  label: string;
  userId: string;
}): Promise<EventAttachment> {
  const { event, label, userId } = params;

  if (!(await canManageEventFiles(event.id))) {
    throw new FlyerFilesAccessError();
  }

  // Round-2 audit (401e3c20, HIGH): a flyer is published only if the CTA is STILL exactly what it
  // encodes when finalize runs (server-checked fingerprint + URL + amount). A change during
  // render/upload discards the candidate and we start over.
  // 5 attempts: one edit-then-revert legitimately uses 3 (stale render, QR refresh, clean save).
  for (let attempt = 1; attempt <= 5; attempt++) {
    const outcome = await saveCTAFlyerOnce(params.cta.id, event, label, userId);
    if (outcome.status === "saved") {
      return outcome.attachment;
    }
  }
  throw new Error(
    "This CTA kept changing while its flyer was being made. Try again; the previous flyer is unchanged.",
  );
}

async function saveCTAFlyerOnce(
  ctaId: string,
  event: Event,
  label: string,
  userId: string,
): Promise<
  { status: "saved"; attachment: EventAttachment } | { status: "stale" }
> {
  // Round-3 audit (39fc50d4, HIGH): the row we print, the event state it is judged against, and the
  // fingerprint finalize will check all come from ONE server statement. Separate reads let an
  // edit-then-revert print a transient promo.
  const { data: snap, error: snapError } = await supabase.rpc(
    "eib_event_cta_render_snapshot",
    { p_cta_id: ctaId },
  );
  if (snapError) {
    throw new Error(errorMessage(snapError));
  }
  const snapshot = snap as {
    cta: EventCallToAction;
    event_state: string | null;
    base_variant?: string | null;
    /** Migration 410 (2026-10-12): events.type, read in the same statement. */
    event_type?: string | null;
    fingerprint: string;
    /** Migration 410 round 6: the artwork the server expects ('standard' | 'insurance:<type>') ... */
    flyer_artwork?: string | null;
    /** ... and the fingerprint finalize checks for a FLYER (URL fingerprint + artwork; equal to it when standard). */
    flyer_fingerprint?: string | null;
  } | null;
  if (!snapshot?.cta || !snapshot.fingerprint) {
    throw new Error("This CTA no longer exists.");
  }
  const cta = snapshot.cta;
  const fingerprint = snapshot.fingerprint;
  if (cta.event_id !== event.id) {
    throw new Error("This CTA doesn't belong to this event.");
  }
  // The variant AND the event type come from the SAME snapshot as the fingerprint finalize checks (migration 410):
  // the variant decides "no amount", the type picks the insurance artwork (owner 2026-10-12). The type is the
  // snapshot's only -- never the caller's possibly-stale event row; a snapshot without it (an older 410 image) leaves
  // it null, which fails CLOSED for an insurance-state CTA (artwork_pending) and is irrelevant for a standard one.
  const baseVariant = toBaseVariant(snapshot.base_variant);
  const noAmount = ctaOmitsDiscount(cta, baseVariant);
  // The event as the SNAPSHOT sees it (its state and type come from the same statement as the row and fingerprint).
  const snapEvent = {
    ...event,
    id: cta.event_id,
    state: snapshot.event_state ?? undefined,
    type: snapshot.event_type ?? null,
  };
  // "button": the most this function is ever allowed to do. Automatic callers gate BEFORE calling it with
  // the strict default, so a pre-cutoff event never gets a flyer without an operator's click.
  // Judged on the snapshot, so an older event whose saved URL went stale is refused here (legacy_stale_url).
  const eligibility = flyerEligibility(cta, snapEvent, {
    mode: "button",
    baseVariant,
  });
  if (!eligibility.ok && eligibility.kind === "artwork_pending") {
    // Typed, so the Repair pass reports it as "waiting for artwork", not as a failure. Nothing was rendered or saved.
    throw new FlyerArtworkPendingError(snapEvent.type);
  }
  if (!eligibility.ok) {
    throw new Error(eligibility.reason);
  }

  // Audit H1 (2ee88b30): never print a saved URL that no longer matches the CTA. Regenerate (itself a
  // conditional publish) and start over with a fresh fingerprint.
  // The saved URL must be exactly what the snapshot's own inputs build -- on an unprepared CTA an edit
  // does not clear a saved URL, so equality with generated_url alone proves nothing.
  const currentUrl = canonicalCTAUrl(cta, snapEvent, baseVariant);
  if (cta.generated_url !== currentUrl) {
    // External audit P2 (35406751): the ONLY QR-write path in this function. An event created before the
    // cutoff never reaches it. Today the eligibility check above already refuses this case (same snapshot URL
    // comparison), so this guard is defence in depth: it keys only on the event's created_at (from the caller's
    // event row -- the snapshot does not carry it; missing/unparseable counts as pre-cutoff), so a later edit
    // to flyerEligibility cannot reopen the rewrite.
    if (isPreCutoffEvent(event)) {
      throw new Error(LEGACY_STALE_URL_REASON);
    }
    try {
      await generateCTAQRCode(cta.id, event, userId);
    } catch (err) {
      throw new Error(
        `This CTA changed since its QR code was made, and the QR code couldn't be refreshed (${errorMessage(err)}). No flyer was made; the previous one is unchanged.`,
      );
    }
    return { status: "stale" };
  }

  const encodedUrl = cta.generated_url as string;
  // An insurance-state flyer shows no amount (the insurance artwork for the snapshot's event type); finalize requires
  // it to declare none.
  // Mig 1480: a gun show / range night with a state offer gets the design the server names (eib:...).
  const serverKey = snapshot.flyer_artwork ?? null;
  const content: FlyerContent =
    serverKey !== null && serverKey.startsWith("eib:")
      ? {
          kind: "server",
          key: serverKey,
          amount: noAmount ? null : Number(cta.voucher_amount),
        }
      : noAmount
        ? { kind: "insurance", eventType: snapEvent.type }
        : { kind: "amount", amount: Number(cta.voucher_amount) };
  // Stamped with the artwork's own DPI so it prints at 8.5x11 in (standard 300, insurance range night 360).
  // Round 6 (external audit fca9b75d P2): the server names the artwork from the SAME snapshot; we must agree with it,
  // and finalize checks the flyer fingerprint (which folds the artwork in), so a render for an event type that changed
  // before finalize is refused as stale. An insurance flyer needs both (fail CLOSED on an older server image); a
  // standard flyer on a server without them uses the URL fingerprint, exactly as before.
  const serverArtwork = snapshot.flyer_artwork ?? null;
  const flyerFingerprint = snapshot.flyer_fingerprint ?? null;
  if (noAmount && (!serverArtwork || !flyerFingerprint)) {
    throw new Error(
      "The server couldn't confirm which flyer design this event needs, so no flyer was made. Reload the page and try again.",
    );
  }
  const { artwork } = flyerArtworkFor(content);
  // Migration 470: the key of the design this browser DRAWS (its manifest entry), compared with the server's and then
  // DECLARED to finalize, which refuses a versioned design that is not declared (an older tab) or a wrong one.
  if (serverArtwork !== null && serverArtwork !== artwork.key) {
    throw new Error(
      `This event's flyer design couldn't be confirmed (expected ${serverArtwork}), so no flyer was made. Reload the page and try again.`,
    );
  }
  const blob = await withPngDpi(
    await renderFlyerPng(encodedUrl, content),
    artwork.dpi,
  );

  let created: EventAttachment;
  try {
    created = await uploadEventAttachment(
      event.id,
      new File([blob], flyerFileName(cta, label), { type: "image/png" }),
    );
  } catch (err) {
    const message = errorMessage(err);
    if (/maximum of \d+ attachments/i.test(message)) {
      throw new Error(
        "This event already has the maximum number of files. Remove one from Files, then create the flyer again. The previous flyer is unchanged.",
      );
    }
    throw new Error(message);
  }

  let retired: { id: string; storage_path: string }[] = [];
  const { data: finData, error: finError } = await supabase.rpc(
    "finalize_event_cta_flyer",
    {
      p_attachment_id: created.id,
      p_cta_id: cta.id,
      // The FLYER fingerprint (round 6); on a server without it (standard flyers only, see above) the URL one.
      p_url_fingerprint: flyerFingerprint ?? fingerprint,
      p_encoded_url: encodedUrl,
      p_amount: noAmount ? null : cta.voucher_amount,
      // Migration 470: the artwork drawn (manifest key). Requires 470 on the server (the RPC has no such argument before).
      p_flyer_artwork: artwork.key,
    },
  );
  if (finError) {
    const code = (finError as { code?: string }).code;
    if (!code) {
      // Round-4 audit (HIGH): no server answer, and a finalize may STILL BE RUNNING (e.g. waiting on
      // the CTA row). A plain read of "unlinked" proves nothing. cancel_event_cta_flyer_candidate takes
      // the same per-CTA lock, so it waits out any in-flight finalize and decides on the locked row.
      const { data: cancelData, error: cancelError } = await supabase.rpc(
        "cancel_event_cta_flyer_candidate",
        { p_attachment_id: created.id, p_cta_id: cta.id },
      );
      const cancel = cancelData as {
        status?: string;
        storage_path?: string;
      } | null;
      if (cancelError || !cancel?.status) {
        throw new Error(
          "The connection dropped while saving the flyer, and it couldn't be confirmed. Check the event's Files before trying again.",
        );
      }
      if (cancel.status === "claimed") {
        return { status: "saved", attachment: created };
      }
      if (cancel.status === "cancelled" && cancel.storage_path) {
        try {
          await deleteFile("supabase", EVENT_ATTACHMENTS_BUCKET, [
            cancel.storage_path,
          ]);
        } catch {
          /* tombstoned; bytes reported by find_orphaned_event_attachment_objects() */
        }
      }
      throw new Error(
        "The connection dropped while saving the flyer; it wasn't saved and the previous flyer is unchanged. Try again.",
      );
    }
    // An explicit server refusal: the finalize rolled back, the previous flyer is untouched, and the
    // candidate is safe to remove.
    try {
      await deleteEventAttachment(created.id);
    } catch {
      /* reported by find_orphaned_event_attachment_objects() */
    }
    if (code === "EIBF1") {
      return { status: "stale" };
    }
    throw new Error(`The flyer couldn't be saved: ${errorMessage(finError)}`);
  }
  retired = ((finData as { retired?: typeof retired } | null)?.retired ??
    []) as typeof retired;

  if (retired.length > 0) {
    try {
      await deleteFile(
        "supabase",
        EVENT_ATTACHMENTS_BUCKET,
        retired.map((r) => r.storage_path),
      );
    } catch (err) {
      // Rows are already tombstoned; leftover bytes are reported by the reconciliation function.
      console.warn("Couldn't remove the previous flyer's file:", err);
    }
  }
  return { status: "saved", attachment: created };
}
